import { useState, useEffect } from 'react'
import { GetServerSideProps } from 'next'

import { List } from '../../components/List/List'
import { ListItemCalendarTicket } from '../../components/List/ListItemCalendarTicket/ListItemCalendarTicket'
import { BestPriceGuarantee } from '../../components/Banners/BestPriceGuarantee/BestPriceGuarantee'
import { NumParksPicker } from '../../components/List/ListItemCalendarTicket/NumParksPicker/NumParksPicker'
import { FloatingCart } from '../../components/Cart/FloatingCart/FloatingCart'
import { ContinueLink } from '../../components/Misc/ContinueLink/ContinueLink'
import { TextApiResponseType, TextType } from '../../types/TextType'
import { PageView } from '../../components/Analytics/PageView/PageView'

import type { ProductType, ProductLeadPriceType, ProductLeadPriceDisplayType } from '../../types/ProductType'
import { BookingSiteOperated } from '../../components/Misc/BookingSiteOperated/BookingSiteOperated'
import { ExpressPassesComponent } from '../../components/Hotels/ExpressPassesComponent/ExpressPassComponent';
import Head from 'next/head';
import { getLeadPricing } from '../../utils/pricingUtils'

const ticketIds = [
  284254, 284255, 284256, // Express Pass
  284257, 284265, 284263, // Express Pass Unlimited
]
const textTypes = ['express_pass_page_intro']

/**
 * We don't have "number of parks" in the product data and it's probably
 * not worth adding, since the field wouldn't be relevant for 99% of our
 * products. So using a bit of a hack to determin the number of parks
 * for the moment - products with "&" in the title are considered to have
 * 2 parks, all other products are considered to have 1.
 */
function getProductsForNumberOfParks(numParks: number, products: ProductType[]) {
  return products.filter(p => numParks === 1 ? !p.title.includes('&') : p.title.includes('&'))
}

function ExpressPass({ products, texts, productLeadPricing }: { products: ProductType[], texts: TextType[],  productLeadPricing: ProductLeadPriceType[] }) {

  const [ numParks, setNumParks ] = useState(1)
  const [ filteredProducts, setFilteredProducts ] = useState(getProductsForNumberOfParks(numParks, products))

  // Filter products by number of of parks when number of parks changes:
  useEffect(() => setFilteredProducts(getProductsForNumberOfParks(numParks, products)), [ numParks, products ])

  const title = "Express Passes"
  const introText = texts.find(t => t.text_type === 'express_pass_page_intro')?.body ?? '';

  const leadPricingData = getLeadPricing(productLeadPricing)

  return (
    <>
      <Head key={'express-passess'}><title>Universal Orlando™ | Express Passess</title></Head>
      <PageView
        pageName='Express Passes'
        productsOnPage={ products }
      />
      <main className='products'>
      <FloatingCart showNextStep={true} currentSection='expressPasses' placement="top" />
        <ContinueLink currentSection='expressPasses' />
        <List title={title} introText={introText} rating={''} expressPassesHotelsInclusions={<ExpressPassesComponent/>}>
          <BookingSiteOperated placement="intro" />
          <NumParksPicker numParks={numParks} setNumParks={setNumParks} />
          {
            filteredProducts.map((product: ProductType) => {

            // Find the lead price using the product id.
            const leadPriceDisplay: ProductLeadPriceDisplayType | undefined = leadPricingData.find(productLeadPrice => productLeadPrice.id == product.id)

              return (
                <ListItemCalendarTicket
                  key={product.id}
                  product={product}
                  isDatedTicket={false}
                  leadPrice={leadPriceDisplay}
                />
              )
            })
          }
        </List>
        <FloatingCart showNextStep={true} currentSection='expressPasses' placement="bottom" />
      </main>
      <BestPriceGuarantee />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {

  const fetches = ticketIds.map(ticketId => fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/products/${ticketId}?view=website`))

  const results = await Promise.all(fetches)

  const products: Array<ProductType | null> = await Promise.all(results.map(result => {
    if (result.statusText === 'OK') {
      return result.json()
    } else {
      return null
    }
  }))

    // Get Lead Prices:
    const leadPriceFetch = ticketIds.map(ticketId => fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/products/lead-price/${ticketId}`))
    const leadPriceResult = await Promise.all(leadPriceFetch);
    // Add each lead price result to Park Ticket props.
    const leadPrice: Array<ProductLeadPriceType | null> = await Promise.all(leadPriceResult.map(result => {
      if (result.statusText === 'OK') {
        return result.json()
      } else {
        return null
      }
    }))

  // Get page text:
  const textResult = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/text?types=${textTypes.join(",")}`)
  const texts: TextApiResponseType = await textResult.json()

  return { props: {
    products: products.filter(p => p !== null),
    texts: texts.data,
    productLeadPricing: leadPrice.filter(l => l !== null)
  }}
}

export default ExpressPass


